import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import styles from './styles.module.scss';
import { MetricChartDotRadius, MetricChartXAxisHeight, MetricChartYAxisWidth } from './constants/index.jsx';
import MetricChartTooltip from './MetricChartTooltip.jsx';
import { MetricChartHeader } from './index.jsx';
import { GenerateNdviTicks } from './libs/index.jsx';
import { BreakpointMobile } from '../../constants/ui';
import { ChartColorsMap } from '../Charts/constants/index.jsx';

const ChartTooltip = ({ t, active, payload, sensor }) => {
  if (!active || !payload || payload.length < 1) return null;
  const degreesDays = t('units.degreesDays');
  const data = payload[0].payload;
  const value = data.value;
  const valueCompared =
    payload.length > 1
      ? [
          { name: sensor.name, value: `${value}${degreesDays}`, color: ChartColorsMap.gdd },
          {
            name: sensor.name2,
            value: `${data.value2}${degreesDays}`,
            color: ChartColorsMap.gddCompared,
          },
        ]
      : null;
  return value !== null && <MetricChartTooltip value={`${value}${degreesDays}`} comparedData={valueCompared} />;
};

const MetricChartGdd = ({ t, title, data, crop, sensor, widthViewport }) => {
  const { selectedSecondarySensor } = useSelector((state) => state.sensors);
  const [domainYAsis, setDomainYAsis] = useState(null);
  const [ticksYAsis, setTicksYAsis] = useState(null);

  // Set domain and ticks
  useEffect(() => {
    const domain = data.domain;
    if (domain && domain.length > 1) {
      setDomainYAsis(domain);
      const ticks = GenerateNdviTicks(domain[0], domain[1], 3);
      setTicksYAsis(ticks);
    }
  }, [data]);

  if (!data) {
    return null;
  }

  return (
    <div className={styles.metricChart}>
      {widthViewport > BreakpointMobile && (
        <MetricChartHeader
          title={title}
          sensorName={sensor.name}
          sensorNameCompared={sensor.name2}
          color={ChartColorsMap.gdd}
          colorCompared={ChartColorsMap.gddCompared}
        />
      )}

      <ResponsiveContainer width="100%" height={140} className="chart-report chart-report--gdd">
        <AreaChart
          width={'100%'}
          height={140}
          data={data.data}
          margin={{
            top: 8,
            right: 8,
            left: 0,
            bottom: 0,
          }}
        >
          <defs>
            <linearGradient id="gradientGdd" x1="0" y1="0" x2="0" y2="1">
              <stop stopColor={ChartColorsMap.gdd} />
              <stop offset="1" stopOpacity="0" stopColor={ChartColorsMap.gdd} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="0 0" stroke={'var(--neutral-8)'} vertical={false} />
          <XAxis dataKey="date" tickLine={false} height={MetricChartXAxisHeight} />
          <YAxis
            ticks={ticksYAsis}
            tickCount={3}
            tickSize={0}
            tickMargin={6}
            width={MetricChartYAxisWidth}
            domain={domainYAsis}
          />
          <Tooltip content={<ChartTooltip sensor={sensor} t={t} />} />
          <Area
            dataKey="value"
            type="monotone"
            baseValue="dataMin"
            strokeWidth={2}
            stroke={ChartColorsMap.gdd}
            fill="url(#gradientGdd)"
            fillOpacity={0.2}
            activeDot={{ r: MetricChartDotRadius, fill: '#fff', stroke: ChartColorsMap.gdd }}
          />
          {selectedSecondarySensor && data.data[0].value2 !== null && (
            <Area
              dataKey="value2"
              type="monotone"
              strokeWidth={2}
              stroke={ChartColorsMap.gddCompared}
              strokeDasharray="2 2"
              fill="url(#gradientGdd)"
              fillOpacity={0.2}
              activeDot={{ r: MetricChartDotRadius, fill: '#fff', stroke: ChartColorsMap.gddCompared }}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
      <div className={`${styles.metricChartUnit} ${styles.metricChartUnitSmall}`}>
        {t('units.degreesDays')}
      </div>
      {!crop && <div className={styles.metricChartEmpty}>{t('empty.noData')}</div>}
    </div>
  );
};

export default MetricChartGdd;
